import { ConfirmationModal, IconAttention, Text, useModal } from '@procurify/ui'

import { toast } from '@webapp/components/Notification'
import { getMessageFromError } from '@webapp/core/utils/errors/getMessageFromError'
import { useProcurifyIntl } from '@webapp/hooks'
import { useMutateDeletePOPDFLabelSet } from '@webapp/modules/purchasing/features/delete-po-pdf-label-set/models'
import { type PDFLabelSet } from '@webapp/modules/vendor/entities/PDFLabelSet' // eslint-disable-line import/no-internal-modules
import messages from './messages'

export interface UseDeletePOPDFLabelSetPresenterOptions {
  /** Post-delete reselection continuation — Feature Set orchestration (BHV-041). */
  onDeleted?: () => void
}

/**
 * Danger confirmation + DELETE for a PDF label set (BHV-041). This
 * confirmation exists in Angular and is kept as a programmatic
 * `ConfirmationModal`.
 */
export const useDeletePOPDFLabelSetPresenter = (
  options: UseDeletePOPDFLabelSetPresenterOptions = {}
) => {
  const intl = useProcurifyIntl()
  const modal = useModal()
  const { mutateAsync, isLoading } = useMutateDeletePOPDFLabelSet()

  const deleteSet = (set: PDFLabelSet) => {
    modal.showModal(ConfirmationModal, {
      open: true,
      icon: <IconAttention />,
      type: 'danger',
      header: intl.formatMessage(messages.confirmationModalTitle),
      content: (
        <Text>{intl.formatMessage(messages.confirmationModalDescription)}</Text>
      ),
      confirmButtonText: intl.formatMessage(
        messages.confirmationModalConfirmButton
      ),
      cancelButtonText: intl.formatMessage(
        messages.confirmationModalCancelButton
      ),
      handleDismiss: () => {
        // Cancel makes no API call
        modal.hideModal()
      },
      onConfirm: async (done: () => void) => {
        try {
          await mutateAsync(set.key)

          toast.success({
            title: intl.formatMessage(messages.successTitle),
            children: intl.formatMessage(messages.successMessage, {
              name: set.value.name,
            }),
          })

          modal.hideModal()
          options.onDeleted?.()
        } catch (error) {
          const errorMessage =
            getMessageFromError(error) ||
            intl.formatMessage(messages.defaultErrorMessage)

          toast.error({
            title: intl.formatMessage(messages.errorTitle),
            children: errorMessage,
          })

          // Keep the modal open/retryable on failure
          done()
        }
      },
    })
  }

  return {
    commands: {
      deleteSet,
    },
    states: {
      isDeleting: isLoading,
    },
  }
}
